import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { useSetClientPinSelfMutation } from '@features/customer-reservations/queries';
import { IconBinocular } from '@shared/components/icons/icon-binocular';
import { FormattedMessage, useIntl } from 'react-intl';

const PRIMARY = '#D02964';

const PublicSetPinPage = () => {
  const intl = useIntl();
  const { userId: profileUserId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPin2, setShowPin2] = useState(false);
  const [pinConfirm, setPinConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { mutate: savePin, isPending } = useSetClientPinSelfMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (pin.length < 4) {
      setFormError(intl.formatMessage({ id: 'publicReservation.pinTooShort' }));
      return;
    }
    if (pin !== pinConfirm) {
      setFormError(intl.formatMessage({ id: 'publicReservation.pinsDoNotMatch' }));
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setFormError(intl.formatMessage({ id: 'publicReservation.invalidResetEmail' }));
      return;
    }
    savePin(
      { pin, email: email.trim() },
      {
        onSuccess: () => {
          navigate(`/public-reservation/profile/${profileUserId}`, { replace: true });
        },
        onError: error => {
          setFormError(error.message);
        },
      },
    );
  };

  return (
    <Box sx={{ minHeight: '100dvh', background: 'linear-gradient(160deg, #fff0f5 0%, #fff 60%)' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #e33f5c 100%)`,
          pt: 2,
          pb: 2.5,
          px: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.85)' }}
            onClick={() => navigate(`/public-reservation/profile/${profileUserId}`)}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="body1" fontWeight={700} sx={{ color: '#fff' }}>
            <FormattedMessage id="publicReservation.pinSetup" />
          </Typography>
        </Stack>
      </Box>

      <Container maxWidth="sm" sx={{ pt: 4, pb: 6 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            <FormattedMessage id="publicReservation.importantNotice" />
          </AlertTitle>
          <FormattedMessage id="publicReservation.pinNotice" />
        </Alert>

        {formError && (
          <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label={intl.formatMessage({ id: 'publicReservation.resetEmail' })}
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              autoComplete="email"
              required
              fullWidth
              disabled={isPending}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack spacing={2} direction="row" alignItems="center">
              <TextField
                label={intl.formatMessage({ id: 'publicReservation.pinLabel' })}
                value={pin}
                onChange={e => setPin(e.target.value)}
                type={showPin ? 'text' : 'password'}
                autoComplete="new-password"
                fullWidth
                autoFocus
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="text"
                onClick={() => setShowPin(prev => !prev)}
                disabled={isPending}
                sx={{ minWidth: '32px' }}
              >
                <IconBinocular />
              </Button>
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center">
              <TextField
                label={intl.formatMessage({ id: 'publicReservation.confirmPin' })}
                type={showPin2 ? 'text' : 'password'}
                value={pinConfirm}
                onChange={e => setPinConfirm(e.target.value)}
                autoComplete="new-password"
                fullWidth
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="text"
                onClick={() => setShowPin2(prev => !prev)}
                disabled={isPending}
                sx={{ minWidth: '32px' }}
              >
                <IconBinocular />
              </Button>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#b0234f' }, borderRadius: '12px' }}
            >
              <FormattedMessage id="publicReservation.savePinAndContinue" />
            </Button>
          </Stack>
        </form>
      </Container>
    </Box>
  );
};

export default PublicSetPinPage;
